import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import AdminFeatureFlagsPage from '../AdminFeatureFlagsPage';
import { PageContainer, PageHeader, TabShell, TabShellTab } from '../../components/ui';
import { useUrlTab } from '../../hooks/useUrlTab';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { tokens } from '../../theme/tokens';
import { systemJson } from './api';
import { LEGACY_TO_V2, SystemV2Tab, V2_TABS } from './legacyTabs';
import { OverviewTab } from './OverviewTab';
import { RepairTab } from './RepairTab';
import { RequestsTab, useRequestBadge } from './RequestsTab';
import { ScriptsTab } from './ScriptsTab';
import { SettingsTab } from './SettingsTab';

const Badge: React.FC<{ count?: number; tone: 'problem' | 'caution'; dot?: boolean }> = ({ count, tone, dot }) => {
  if (!dot && !count) return null;
  return (
    <Box component="span" sx={{ ml: 0.75, minWidth: dot ? 8 : 18, height: dot ? 8 : 18, px: dot ? 0 : 0.6, borderRadius: '9px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', bgcolor: tokens.signal[tone].solid, color: tokens.ink.inverse, fontSize: 11, fontWeight: 700, verticalAlign: 'middle' }}>
      {dot ? '' : count}
    </Box>
  );
};

export const AdminSystemV2Page: React.FC = () => {
  const { t } = useAppTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get('tab');
  useEffect(() => {
    const mapped = raw ? LEGACY_TO_V2[raw] : undefined;
    if (!mapped) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (mapped === 'overview') next.delete('tab');
      else next.set('tab', mapped);
      return next;
    }, { replace: true });
  }, [raw, setSearchParams]);

  const [tab, setTab] = useUrlTab(V2_TABS, 'overview');
  const [settingsDirty, setSettingsDirty] = useState(0);
  const onSettingsDirty = useCallback((count: number) => setSettingsDirty(count), []);
  const requests = useRequestBadge();
  const release = useQuery({
    queryKey: ['admin-release-steps'],
    queryFn: () => systemJson<{ stack: { projectId: string; billingRegion: string }; steps: Array<{ lastApply: unknown }> }>('/admin/release-steps'),
  });
  const unapplied = release.data ? release.data.steps.filter((step) => !step.lastApply).length : null;
  const stack = release.data?.stack;

  const label = (key: SystemV2Tab, text: string, extra?: React.ReactNode) => (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
      {text}
      {extra}
    </Box>
  );

  const tabs: TabShellTab<SystemV2Tab>[] = [
    { key: 'overview', label: label('overview', t('admin.systemV2.tabs.overview')) },
    { key: 'requests', label: label('requests', t('admin.systemV2.tabs.requests'), <Badge count={requests.open} tone={requests.urgent ? 'problem' : 'caution'} />) },
    { key: 'settings', label: label('settings', t('admin.systemV2.tabs.settings'), <Badge dot={settingsDirty > 0} tone="caution" />) },
    { key: 'flags', label: label('flags', t('admin.systemV2.tabs.flags')) },
    { key: 'scripts', label: label('scripts', t('admin.systemV2.tabs.scripts'), <Badge count={unapplied || 0} tone="caution" />) },
    { key: 'repair', label: label('repair', t('admin.systemV2.tabs.repair')) },
  ];

  return (
    <PageContainer>
      <PageHeader
        title={t('admin.systemV2.title')}
        description={t('admin.systemV2.subtitle')}
        actions={stack ? (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, height: 30, px: 1.25, borderRadius: `${tokens.radius.small}px`, border: `1px solid ${tokens.accent.tintStrong}`, bgcolor: tokens.accent.tint }}>
            <Box component="span" aria-hidden sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: tokens.accent.main }} />
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: tokens.accent.hover }}>{t('admin.systemV2.stack', { region: stack.billingRegion })}</Typography>
            <Typography sx={{ fontSize: 11.5, fontFamily: tokens.font.mono, color: tokens.ink.bodyAlt }}>{stack.projectId}</Typography>
          </Box>
        ) : undefined}
      />
      <TabShell tabs={tabs} value={tab} onChange={setTab} aria-label={t('admin.systemV2.title')}>
        {tab === 'overview' && (
          <OverviewTab
            onOpen={setTab}
            requestsOpen={requests.open}
            privacyUrgent={requests.urgent}
            settingsDirty={settingsDirty > 0}
            unappliedScripts={unapplied}
          />
        )}
        {tab === 'requests' && <RequestsTab />}
        {tab === 'settings' && <SettingsTab onDirty={onSettingsDirty} />}
        {tab === 'flags' && <AdminFeatureFlagsPage variant="v2" embedded />}
        {tab === 'scripts' && (
          <ScriptsTab
            regionLabel={stack ? t('admin.systemV2.scripts.stackOnly', { region: stack.billingRegion, project: stack.projectId }) : t('admin.systemV2.scripts.stackOnlyUnknown')}
          />
        )}
        {tab === 'repair' && <RepairTab />}
      </TabShell>
    </PageContainer>
  );
};
